import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import {
  Snackbar,
  Alert,
  IconButton,
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import DatePickerField from "../../../common/DatePickerDashboard";
import NoDataFound from "../../../common/NoDataFound";
import {
  getAllTaskLinearAction,
  deleteTaskActionById,
  searchTaskLinearAction,
  getClientCustomerId,
  getCustomerFromPolicyAction,
} from "../../../redux/actions/taskActions";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import Loader from "../../loader/loader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TaskCalendar from "./TaskCalendar";
import { useTheme, useMediaQuery } from "@mui/material";
import { CustomTableCell } from "../../../common/CustomTableCell";

const SnackbarComponent = ({ open, message, severity, onClose }) => (
  <Snackbar
    open={open}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
);

const DashboardTaskList = ({ triggerFetch = false }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const [isEditTask, setIsEditTask] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: null,
    toDate: null,
    query: "",
  });
  const [taskComponentOpen, setTaskComponentOpen] = useState(false);
  const [taskComponentMounted, setTaskComponentMounted] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedEventData, setSelectedEventData] = useState(null);
  const [getTaskData, setGetTaskData] = useState([]);
  const [forceRefreshKey, setForceRefreshKey] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const hasBootedRef = useRef(false);
  const currentFiltersRef = useRef(null);
  const requestInProgressRef = useRef(false);

  const getToday = () => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(today.getDate()).padStart(2, "0")}`
    );
  };
  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}-01`
    );
  };
  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const taskData = useSelector(
    (state) => state?.task?.allTaskLinear.content || [],
    shallowEqual
  );
  const pagination = useSelector((state) => state?.task?.allTaskLinear || []);
  const clientCustomer = useSelector((state) => state.task.clientCustomer);

  const forceDataRefresh = useCallback(() => {
    if (requestInProgressRef.current) {
      return;
    }

    if (!isFiltered) {
      return;
    }

    requestInProgressRef.current = true;
    setTableLoading(true);
    setLastUpdateTime(Date.now());

    const fromDateISO = appliedFilters.fromDate
      ? dayjs(appliedFilters.fromDate).format("YYYY-MM-DD")
      : "";
    const toDateISO = appliedFilters.toDate
      ? dayjs(appliedFilters.toDate).format("YYYY-MM-DD")
      : "";
    const cleanQuery = (appliedFilters.query || "").trim();
    const cacheBreaker = `_cb=${Date.now()}`;
    const actionPromise = dispatch(
            searchTaskLinearAction(
        {
          fromDate: fromDateISO,
          toDate: toDateISO,
          query: cleanQuery,
          cacheBreaker,
        },
        page,
        rowsPerPage
      )
    );

    if (actionPromise && typeof actionPromise.finally === "function") {
      actionPromise.finally(() => {
        requestInProgressRef.current = false;
        setTableLoading(false);
        setForceRefreshKey((prev) => prev + 1);
      });
    } else {
      setTimeout(() => {
        requestInProgressRef.current = false;
        setTableLoading(false);
        setForceRefreshKey((prev) => prev + 1);
      }, 1000);
    }
  }, [dispatch, page, rowsPerPage, isFiltered, appliedFilters]);


  useEffect(() => {
    if (!triggerFetch || hasBootedRef.current) return;
    hasBootedRef.current = true;
    setTimeout(() => {
      forceDataRefresh();
    }, 100);
  }, [triggerFetch, forceDataRefresh]);

  useEffect(() => {
    if (!hasBootedRef.current) return;

    const currentFilters = {
      page,
      rowsPerPage,
      isFiltered,
      appliedFilters: JSON.stringify(appliedFilters),
    };

    if (JSON.stringify(currentFilters) === JSON.stringify(currentFiltersRef.current)) {
      return;
    }

    currentFiltersRef.current = currentFilters;
    forceDataRefresh();
  }, [page, rowsPerPage, isFiltered, appliedFilters, forceDataRefresh]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const handleTaskComponentClose = useCallback(() => {
    setTaskComponentOpen(false);
    setTimeout(() => {
      setTaskComponentMounted(false);
      setIsEditTask(false);
      setEditingEventId(null);
      setSelectedEventData(null);
    }, 300);
  }, []);

  const handleEdit = useCallback(
    (task) => {
      if (!task) return;

      try {
        setIsEditTask(true);
        setEditingEventId(task.id);
        setGetTaskData(task);

        let firstName = "";
        let lastName = "";
        if (task.customerName) {
          const nameParts = task.customerName.split(" ");
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(" ");
        }

        const selectedEventData = {
          id: task.id,
          clientCustomerId: task.clientCustomerId,
          policyId: task.policyId,
          dueDate: task.dueDate,
          taskType: task.taskType,
          assignedToName: task.assignedToName,
          assignedTo: task.assignedTo,
          status: task.status,
          taskPriority: task.priority,
          taskDescription: task.taskDescription,
          policyNumber: task.policyId ? { id: task.policyId } : null,
          customer: task.clientCustomerId
            ? {
                id: task.clientCustomerId,
                customerNumber: task.customerNumber || "",
                firstName: firstName || "",
                lastName: lastName || "",
              }
            : null,
        };

        setSelectedEventData(selectedEventData);

        setTimeout(() => {
          setTaskComponentOpen(true);
          setTaskComponentMounted(true);
        }, 50);

        if (task.clientCustomerId) {
          dispatch(getClientCustomerId(task.clientCustomerId));
        }

        const policyIdExists =
          task.policyId &&
          task.policyId !== null &&
          task.policyId !== undefined &&
          task.policyId !== "" &&
          task.policyId !== "null" &&
          task.policyId !== "undefined";

        if (policyIdExists) {
          dispatch(getCustomerFromPolicyAction(task.policyId))
            .then((response) => {
              if (response?.payload) {
                const policyData = response.payload;
                setSelectedEventData((prev) => ({
                  ...prev,
                  policyNumber: {
                    id: task.policyId,
                    policyNumber: policyData.policyNumber || task.policyId,
                  },
                  customer: {
                    id: policyData.clientCustomerId || task.clientCustomerId,
                    firstName: policyData.firstName || firstName,
                    lastName: policyData.lastName || lastName,
                    customerNumber:
                      policyData.customerNumber || task.customerNumber || "",
                  },
                }));
              }
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error("Error in handleEdit:", error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (clientCustomer) {
      setSelectedEventData((prev) =>
        prev
          ? {
              ...prev,
              firstName: clientCustomer.firstName,
              lastName: clientCustomer.lastName,
            }
          : null
      );
    }
  }, [clientCustomer]);

  const handleTaskSubmission = useCallback(
    (formValues) => {
      if (formValues) {
        handleTaskComponentClose();
        setSnackbarMessage("Task updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          forceDataRefresh();
        }, 1000);
      }
    },
    [handleTaskComponentClose, forceDataRefresh]
  );

  const handleTaskDeletion = useCallback(() => {
    if (editingEventId) {
      dispatch(deleteTaskActionById(editingEventId))
        .then(() => {
          setSnackbarMessage("Task deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setTimeout(() => {
            forceDataRefresh();
          }, 1000);
        })
        .catch(() => {
          setSnackbarMessage("Failed to delete task! Please try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });

      handleTaskComponentClose();
    }
  }, [editingEventId, dispatch, handleTaskComponentClose, forceDataRefresh]);

  const handleClose = () => {
    setOpen(false);
    setDetailTask(null);
  };

  const handleOpenDialog = (id) => {
    setTaskToDelete(id);
    setOpenDialog(true);
  };

  const handleDeleteTask = () => {
    if (!taskToDelete) return;
    
    dispatch(deleteTaskActionById(taskToDelete))
      .then(() => {
        setSnackbarMessage("Task deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        setTimeout(() => {
          forceDataRefresh();
        }, 1000);
      })
      .catch(() => {
        setSnackbarMessage("Failed to delete task! Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setOpenDialog(false);
        setTaskToDelete(null);
      });
  };

  const handleDateChange = (fieldName, value) =>
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const handleApply = () => {
    const hasDate = formData.fromDate || formData.toDate;
    const hasSearch = (searchQuery || "").trim() !== "";
    if (!hasDate && !hasSearch) return;

    setAppliedFilters({
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      query: searchQuery,
    });

    setIsFiltered(true);
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery("");
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });

    setAppliedFilters({
      fromDate: null,
      toDate: null,
      query: "",
    });

    setIsFiltered(false);
    setPage(0);
    setFieldErrors({});
    setTouchedFields({});
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
  };

  const handleUpdateTask = (id) => {
    const selectedRow = taskData.find((t) => t.id === id);
    if (selectedRow) {
      handleEdit(selectedRow);
    }
  };

  const getShortDescription = (text) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > 3 ? words.slice(0, 3).join(" ") + "..." : text;
  };

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity || "success");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const CustomTaskComponent = useMemo(() => {
    const isEditingTask = selectedEventData && selectedEventData.id;

    if (!taskComponentMounted) return null;

    return (
      <TaskCalendar
        taskComponentOpen={taskComponentOpen}
        isEditTask={isEditTask}
        setIsEditTask={setIsEditTask}
        setTaskComponentOpen={setTaskComponentOpen}
        open={taskComponentOpen}
        handleClose={handleTaskComponentClose}
        initialValues={selectedEventData || {}}
        onSubmit={handleTaskSubmission}
        onDelete={handleTaskDeletion}
        isEditing={isEditingTask ? selectedEventData : null}
        editId={editingEventId}
        handleSnackbar={(msg, sev) => {
          setSnackbarMessage(msg);
          setSnackbarSeverity(sev || "success");
          setSnackbarOpen(true);
        }}
        isLoading={false}
        setIsLoading={() => {}}
        getTaskData={getTaskData}
      />
    );
  }, [
    taskComponentMounted,
    taskComponentOpen,
    isEditTask,
    selectedEventData,
    editingEventId,
    getTaskData,
    handleTaskComponentClose,
    handleTaskSubmission,
    handleTaskDeletion,
  ]);

  return (
    <Box
      sx={{
        padding: isMobile ? "8px" : "24px",
        overflowX: "auto",
        backgroundColor: "#ffffff",
        minHeight: "100vh",
      }}
    >
      <Box mb={3} p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: "flex-start",
            mb: 1.5,
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ width: isMobile ? "100%" : 215, minHeight: 85 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: "left" }}>
              From Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="fromDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: 55 },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ width: isMobile ? "100%" : 215, minHeight: 85 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: "left" }}>
              To Date
            </Typography>
            <DatePickerField
              fieldName="toDate"
              formData={formData}
              handleDateChange={handleDateChange}
              handleBlur={handleBlur}
              touchedFields={touchedFields}
              fieldErrors={fieldErrors}
              size={isMobile ? "small" : "medium"}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: 55 },
              }}
            />
          </Box>

          <Box sx={{ width: isMobile ? "100%" : 200, minHeight: 85 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: "left" }}>
              Task Search
            </Typography>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "5px",
                "& .MuiInputBase-root": { height: 55 },
              }}
              size={isMobile ? "small" : "medium"}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: isMobile ? "100%" : "auto",
              minHeight: 85,
              alignItems: "flex-end",
              mt: -0.5,
              "& button": {
                height: 55,
                minWidth: isMobile ? "calc(50% - 8px)" : 120,
              },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleApply}
              id="applybutton"
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              id="reset-button"
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Box>

      {!isFiltered ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            backgroundColor: "#f9f9f9",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <NoDataFound
            discription="Please use the search filters above to fetch and display Updated Linear View data."
            message="After click Apply- results will shown as per selected filter"
          />
        </Box>
      ) : taskData.length === 0 && !tableLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            backgroundColor: "#f9f9f9",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <NoDataFound
            title="No Data Found"
            discription="Please change search filters range where data is available as per defined range"
            message="After click Apply- results will shown as per selected filter"
          />
        </Box>
      ) : (
        <>
          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? "60vh" : "none",
              overflow: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: isMobile ? "100%" : 650,
                "& .MuiTableCell-root": {
                  fontSize: isMobile ? "12px" : "14px",
                  padding: isMobile ? "8px" : "12px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? 100 : 200,
                },
              }}
              aria-label="task table"
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  {[
                    "Type of Task",
                    "Assigned To",
                    "Created By",
                    "Created Date",
                    "Due Date",
                    "Updated By",
                    "Updated Date",
                    "Status",
                    "Priority",
                    "Closed Date",
                    "Closed By",
                    "Description",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      className="styled-table-heading"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        fontSize: isMobile ? "12px" : "14px",
                        padding: isMobile ? "8px" : "12px",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    className="styled-table-heading"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                      position: "sticky",
                      top: 0,
                      fontSize: isMobile ? "12px" : "14px",
                      padding: isMobile ? "8px" : "12px",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={13}>
                      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                        <Loader />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : taskData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13}>
                      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                        <Typography variant="subtitle1" color="textSecondary">
                          No Data found.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  taskData.map((row) => (
                    <TableRow key={`${row.id}-${forceRefreshKey}`} className="styled-table-row">
                      <TableCell>{row.taskType}</TableCell>
                      <TableCell>{row.assignedToName}</TableCell>
                      <TableCell>{row.createdBy?.name || "-"}</TableCell>
                      <CustomTableCell timestamp={row.createdBy?.timestamp} />
                      <TableCell>
                        {row.dueDate ? formatDate(row.dueDate) : ""}
                      </TableCell>
                      <TableCell>{row.updatedBy?.name || "-"}</TableCell>
                      <CustomTableCell timestamp={row.updatedBy?.timestamp} />
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.priority}</TableCell>
                      <CustomTableCell timestamp={row.closedDate} />
                      <TableCell>{row.closedByName || ""}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={row.taskDescription || ""}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "white",
                                color: "black",
                                boxShadow: 1,
                                border: "1px solid #ccc",
                                fontSize: 14,
                                fontWeight: "bold",
                              },
                            },
                            arrow: { sx: { color: "white" } },
                          }}
                        >
                          <span>{getShortDescription(row.taskDescription)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ pl: 0 }}>
                        <IconButton
                          onClick={() => handleUpdateTask(row.id)}
                          sx={{ ml: -1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleOpenDialog(row.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination?.totalElements > 0 && (
            <Box sx={{ width: "100%", overflow: "hidden", py: 2 }}>
              <Stack
                direction={isMobile ? "column" : "row"}
                justifyContent="flex-end"
                alignItems="center"
                flexWrap="wrap"
                spacing={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isMobile ? "12px" : "14px", mr: 1 }}
                  >
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                    sx={{
                      fontSize: isMobile ? "12px" : "14px",
                      minWidth: isMobile ? 70 : 80,
                      height: isMobile ? 32 : 40,
                    }}
                  >
                    {[5, 10, 15, 25, 50, 100].map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Pagination
                  count={pagination.totalPages}
                  page={page + 1}
                  onChange={(e, value) => handleChangePage(e, value - 1)}
                  showFirstButton
                  showLastButton
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={isMobile ? 0 : 1}
                  size={isMobile ? "small" : "medium"}
                />
              </Stack>
            </Box>
          )}
        </>
      )}

      <Dialog open={openDialog} onClose={(event, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") { return; } setOpenDialog(false); }} disableScrollLock={true} keepMounted={false} hideBackdrop={false} style={{ zIndex: 1300 }}>
        <DialogTitle 
          className="btn-primary"
          sx={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
          }}
        >
          Delete Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2 }}>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTask}
            variant="outlined"
            autoFocus
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {CustomTaskComponent}

      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default DashboardTaskList;